import { useState } from "react";
import { Link } from "react-router-dom";
import { Play } from "lucide-react";
import { useFetch, fileUrl, fmtDate, usePageTitle } from "@/lib/api";
import { MEDIA_CATS, IMG } from "@/lib/content";
import { Loading, EmptyState, Reveal } from "@/components/site/Bits";

const StoryCard = ({ a, i }) => (
  <Reveal delay={(i % 3) * 0.06}>
    <Link to={`/media/${a.id}`} className="group block" data-testid={`article-card-${a.id}`}>
      <div className="relative aspect-video overflow-hidden bg-ink">
        <img src={a.cover_url ? fileUrl(a.cover_url) : IMG.lights} alt="" loading="lazy" className="h-full w-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-105" />
        {a.video_url && <span className="absolute left-3 bottom-3 flex h-10 w-10 items-center justify-center bg-gold text-navy"><Play className="h-4 w-4 fill-current" /></span>}
      </div>
      <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{a.category} • {fmtDate(a.date)}</p>
      <h3 className="mt-1 font-display text-2xl font-black uppercase italic leading-tight group-hover:text-gold transition-colors">{a.title}</h3>
      {a.excerpt && <p className="mt-2 text-sm text-slate-400 line-clamp-2">{a.excerpt}</p>}
    </Link>
  </Reveal>
);

export default function Media() {
  usePageTitle("Media Center");
  const { data, loading } = useFetch("/content/articles");
  const [cat, setCat] = useState("All");
  const all = data || [];
  const list = cat === "All" ? all : all.filter((a) => a.category === cat);
  const [top, ...rest] = list;
  return (
    <div data-testid="media-page" className="bg-pitch">
      <section className="pt-32 sm:pt-36 border-b border-line bg-navy">
        <div className="wrap pb-6 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <p className="eyebrow">Tri County Allstars</p>
            <h1 className="h-display text-5xl sm:text-7xl mt-2">Media <span className="text-gold">Center</span></h1>
          </div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">News • Highlights • Coverage</p>
        </div>
        <div className="wrap flex gap-1 overflow-x-auto no-scrollbar" data-testid="media-category-tabs">
          {["All", ...MEDIA_CATS].map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`media-tab-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`shrink-0 px-4 py-3 text-xs font-bold uppercase tracking-wider border-b-4 transition-colors ${cat === c ? "border-gold text-gold" : "border-transparent text-slate-300 hover:text-white"}`}>
              {c}
            </button>
          ))}
        </div>
      </section>
      <section className="py-12 sm:py-16">
        <div className="wrap">
          {loading ? <Loading /> : !top ? (
            <EmptyState testid="media-empty" title="No stories in this section yet" body="New coverage will be published here by Tri County Allstars staff." />
          ) : (
            <>
              <div className="grid gap-8 lg:grid-cols-[1.6fr_1fr]">
                <Link to={`/media/${top.id}`} className="group relative block aspect-[16/10] overflow-hidden bg-ink" data-testid="media-top-story">
                  <img src={top.cover_url ? fileUrl(top.cover_url) : IMG.night} alt="" className="absolute inset-0 h-full w-full object-cover opacity-75 transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-pitch via-pitch/30 to-transparent" />
                  <div className="absolute bottom-0 p-6 sm:p-10">
                    <span className="bg-[#CC0000] px-2 py-1 font-mono text-[10px] uppercase tracking-widest">Top Story • {top.category}</span>
                    <h2 className="mt-4 h-display text-4xl sm:text-6xl max-w-3xl group-hover:text-gold transition-colors">{top.title}</h2>
                    {top.excerpt && <p className="mt-3 max-w-xl text-sm sm:text-base text-slate-300">{top.excerpt}</p>}
                  </div>
                </Link>
                <div className="space-y-px bg-line/60 self-start" data-testid="media-headlines">
                  <p className="bg-pitch pb-3 eyebrow">Headlines</p>
                  {rest.slice(0, 5).map((a) => (
                    <Link key={a.id} to={`/media/${a.id}`} className="block bg-pitch py-4 group" data-testid={`media-headline-${a.id}`}>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{a.category}</p>
                      <p className="font-display text-xl font-extrabold uppercase italic leading-tight group-hover:text-gold">{a.title}</p>
                    </Link>
                  ))}
                  {rest.length === 0 && <p className="bg-pitch py-4 text-sm text-slate-500">More headlines coming soon.</p>}
                </div>
              </div>
              {rest.length > 0 && (
                <div className="mt-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-3" data-testid="media-grid">
                  {rest.map((a, i) => <StoryCard key={a.id} a={a} i={i} />)}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
